import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiStar } from 'react-icons/fi';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "AI Researcher",
      company: "TechCorp",
      avatar: "data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23a855f7'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='white' font-size='40' font-family='Inter'%3ESC%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "Bervin has revolutionized how I interact with AI models. Being able to compare responses from multiple AIs simultaneously has dramatically improved my research efficiency.",
      gradient: "from-secondary-500 to-purple-600"
    },
    {
      id: 2,
      name: "Marcus Johnson",
      role: "Product Manager",
      company: "InnovateLabs",
      avatar: "data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='50' fill='%2306b6d4'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='white' font-size='40' font-family='Inter'%3EMJ%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "The custom AI personalities feature is a game-changer. I've created specialized assistants for different aspects of product development, and they're incredibly helpful.",
      gradient: "from-accent-500 to-cyan-600"
    },
    {
      id: 3,
      name: "Elena Rodriguez",
      role: "Content Creator",
      company: "Creative Studio",
      avatar: "data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='50' fill='%2322d3ee'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='white' font-size='40' font-family='Inter'%3EER%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "As a content creator, having access to multiple AI perspectives in one place has transformed my creative process. The smart summaries help me synthesize ideas quickly.",
      gradient: "from-primary-500 to-blue-600"
    },
    {
      id: 4,
      name: "David Kim",
      role: "Software Engineer",
      company: "CodeWorks",
      avatar: "data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23f59e0b'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='white' font-size='40' font-family='Inter'%3EDK%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "The lightning-fast responses and clean interface make Bervin my go-to tool for code reviews and technical discussions. It's like having a team of expert consultants.",
      gradient: "from-yellow-500 to-orange-600"
    },
    {
      id: 5,
      name: "Aisha Patel",
      role: "Marketing Director",
      company: "GrowthCo",
      avatar: "data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='50' cy='50' r='50' fill='%2310b981'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='white' font-size='40' font-family='Inter'%3EAP%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "Bervin's privacy-first approach gives me confidence when discussing sensitive marketing strategies. The multi-AI insights have led to breakthrough campaign ideas.",
      gradient: "from-green-500 to-emerald-600"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <section className="py-20 px-6 relative overflow-hidden">
      <div className="container mx-auto">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-30">
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/4 left-1/6 w-64 h-64 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], rotate: [360, 180, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"
          />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 relative z-10"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl font-display font-bold mb-6"
          >
            <span className="gradient-text">What Users Say</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Join thousands of professionals who have transformed their AI interactions with Bervin
          </motion.p>
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative h-96 md:h-80 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-full max-w-3xl">
                  {/* Main Testimonial Card */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="relative p-8 md:p-12 glass border border-white/10 rounded-3xl shadow-2xl hover-lift"
                  >
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${testimonials[currentIndex].gradient} opacity-5 rounded-3xl`}></div>
                    
                    {/* Quote Icon */}
                    <div className="absolute -top-4 left-8">
                      <div className={`w-8 h-8 bg-gradient-to-r ${testimonials[currentIndex].gradient} rounded-full flex items-center justify-center`}>
                        <span className="text-white text-2xl font-bold">"</span>
                      </div>
                    </div>

                    {/* Stars */}
                    <div className="flex justify-center mb-6">
                      {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
                        >
                          <FiStar className="w-6 h-6 text-yellow-400 fill-current" />
                        </motion.div>
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      className="text-lg md:text-xl text-gray-300 text-center leading-relaxed mb-8 italic"
                    >
                      "{testimonials[currentIndex].text}"
                    </motion.p>

                    {/* User Info */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      className="flex items-center justify-center space-x-4"
                    >
                      <div className="relative">
                        <img
                          src={testimonials[currentIndex].avatar}
                          alt={testimonials[currentIndex].name}
                          className="w-16 h-16 rounded-full border-2 border-white/20"
                        />
                        <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${testimonials[currentIndex].gradient} opacity-20 blur-sm`}></div>
                      </div>
                      <div className="text-center">
                        <h4 className="text-white font-bold text-lg">
                          {testimonials[currentIndex].name}
                        </h4>
                        <p className="text-gray-400">
                          {testimonials[currentIndex].role} at {testimonials[currentIndex].company}
                        </p>
                      </div>
                    </motion.div>

                    {/* Glow Effect */}
                    <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${testimonials[currentIndex].gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`}></div>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handlePrevious}
              aria-label="Previous testimonial"
              title="Previous testimonial"
              className="p-3 glass border border-white/20 rounded-full text-gray-300 hover:text-white transition-colors duration-300"
            >
              <FiChevronLeft className="w-6 h-6" />
            </motion.button>

            {/* Dots Indicator */}
            <div className="flex space-x-3">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                  title={`Go to testimonial ${index + 1}`}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? `bg-gradient-to-r ${testimonials[currentIndex].gradient} shadow-lg`
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleNext}
              aria-label="Next testimonial"
              title="Next testimonial"
              className="p-3 glass border border-white/20 rounded-full text-gray-300 hover:text-white transition-colors duration-300"
            >
              <FiChevronRight className="w-6 h-6" />
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 relative z-10"
        >
          {[
            { number: "50K+", label: "Happy Users" },
            { number: "4.9/5", label: "Average Rating" },
            { number: "99.9%", label: "Uptime" },
            { number: "24/7", label: "Support" }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
              className="text-center p-6 glass border border-white/10 rounded-xl hover-lift"
            >
              <div className="text-2xl md:text-3xl font-bold gradient-text mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
